"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslations } from "next-intl";
import Image from "next/image";
import clsx from "clsx";
import IconChevronRight from "@ui/central-icons/icons/IconChevronRight";
import IconCloudSimpleUpload from "@ui/central-icons/icons/IconCloudSimpleUpload";
import IconCrossSmall from "@ui/central-icons/icons/IconCrossSmall";
import IconLoader from "@ui/central-icons/icons/IconLoader";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { Button } from "@ui/components/core/Button";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/components/core/Tooltip";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { EventName } from "@mw/shared/analyticsEventNames";
import { publicUrls } from "@/constants";
import type {
  FlowPortModality,
  FlowTemplateExample,
  FlowTemplatePort,
  FlowTemplateSchema,
} from "@/app/_api/flowTemplatesServer";
import { FlowTemplateModalityIcon } from "./FlowTemplateModalityIcon";
import { isProcessingStep, type ContentWorkflowStep } from "./types";

const ACCEPTED_MIME_TYPES: Record<Exclude<FlowPortModality, "text">, string> = {
  image: "image/jpeg,image/png,image/webp,image/gif",
  video: "video/mp4,video/quicktime,video/webm",
  audio: "audio/mpeg,audio/wav,audio/webm,audio/ogg",
};

function capitalizeFirst(value: string | null | undefined): string {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export interface PrefilledAsset {
  assetId: string;
  previewUrl: string;
}

export interface FlowTemplateFormCardProps {
  flowTemplate: FlowTemplateSchema;
  flowTemplateExample: FlowTemplateExample | null;
  step: ContentWorkflowStep;
  error: string | null;
  validateFile: (file: File) => Promise<string | null>;
  onFlowSubmit: (form: {
    files: Record<string, File>;
    texts: Record<string, string>;
    prefilledAssetsByPort: Record<string, PrefilledAsset>;
  }) => void;
  rateLimited?: boolean;
  isVideo?: boolean;
  redirectCtaUrl?: string | null;
  templateCategory?: string;
  variantSlug?: string;
}

export function FlowTemplateFormCard({
  flowTemplate,
  flowTemplateExample,
  step,
  error,
  validateFile,
  onFlowSubmit,
  rateLimited = false,
  isVideo = false,
  redirectCtaUrl,
  templateCategory,
  variantSlug,
}: FlowTemplateFormCardProps) {
  const t = useTranslations("ClientKeys.creative_templates");
  const isProcessing = isProcessingStep(step);
  const fieldsDisabled = isProcessing || rateLimited;

  const [files, setFiles] = useState<Record<string, File>>({});
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [prefilledAssetsByPort, setPrefilledAssetsByPort] = useState<Record<string, PrefilledAsset>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Per-port version counter — increments on each pick so a slow validation
  // for an earlier file can't clobber state for a later one.
  const fileVersionRef = useRef<Record<string, number>>({});

  const handleFileChange = useCallback(
    async (portId: string, file: File | null) => {
      const version = (fileVersionRef.current[portId] ?? 0) + 1;
      fileVersionRef.current[portId] = version;

      setFieldErrors(prev => {
        if (!prev[portId]) return prev;
        const { [portId]: _removed, ...rest } = prev;
        return rest;
      });
      if (!file) {
        setFiles(prev => {
          const { [portId]: _removed, ...rest } = prev;
          return rest;
        });
        return;
      }
      const validationError = await validateFile(file);
      if (fileVersionRef.current[portId] !== version) return;
      if (validationError) {
        setFieldErrors(prev => ({ ...prev, [portId]: validationError }));
        // Drop any previously-valid file so the form state matches the visible
        // error banner — otherwise submit would proceed with the stale file.
        // FlowFileField's effect revokes the prior objectURL when `file` flips
        // to null, so no extra cleanup needed here.
        setFiles(prev => {
          if (!(portId in prev)) return prev;
          const { [portId]: _removed, ...rest } = prev;
          return rest;
        });
        return;
      }
      setFiles(prev => ({ ...prev, [portId]: file }));
      // Re-clear the port error after the await: a Generate click during the
      // async validation gap may have set a stale "required" error (line 91's
      // clear ran before validateFile resolved), which would otherwise block
      // the next submit even though we now have a valid file.
      setFieldErrors(prev => {
        if (!prev[portId]) return prev;
        const { [portId]: _removed, ...rest } = prev;
        return rest;
      });
      // User-uploaded file wins over a prefilled example asset on the same port.
      setPrefilledAssetsByPort(prev => {
        if (!(portId in prev)) return prev;
        const { [portId]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [validateFile],
  );

  const handleTextChange = useCallback((portId: string, value: string) => {
    setFieldErrors(prev => {
      if (!prev[portId]) return prev;
      const { [portId]: _removed, ...rest } = prev;
      return rest;
    });
    setTexts(prev => ({ ...prev, [portId]: value }));
  }, []);

  const handleUseExampleAsset = useCallback((portId: string, asset: PrefilledAsset) => {
    setFieldErrors(prev => {
      if (!prev[portId]) return prev;
      const { [portId]: _removed, ...rest } = prev;
      return rest;
    });
    setFiles(prev => {
      if (!(portId in prev)) return prev;
      const { [portId]: _removed, ...rest } = prev;
      return rest;
    });
    setPrefilledAssetsByPort(prev => ({ ...prev, [portId]: asset }));
  }, []);

  const handleRemovePrefilledAsset = useCallback((portId: string) => {
    setPrefilledAssetsByPort(prev => {
      if (!(portId in prev)) return prev;
      const { [portId]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (isProcessing) return;

      // Don't overwrite a specific error already shown by handleFileChange
      // (e.g. "File is too large") with the generic "required" message just
      // because the invalid file was dropped from state.
      const requiredPortIds: string[] = [];
      for (const port of flowTemplate.input_ports) {
        if (port.modality === "text") {
          if (!texts[port.id]?.trim()) requiredPortIds.push(port.id);
        } else if (!files[port.id] && !prefilledAssetsByPort[port.id]) {
          requiredPortIds.push(port.id);
        }
      }
      if (requiredPortIds.length > 0) {
        setFieldErrors(prev => {
          const next = { ...prev };
          for (const portId of requiredPortIds) {
            if (!next[portId]) next[portId] = t("flow_field_required");
          }
          return next;
        });
        return;
      }
      if (Object.values(fieldErrors).some(Boolean)) return;

      onFlowSubmit({ files, texts, prefilledAssetsByPort });
    },
    [flowTemplate.input_ports, files, texts, prefilledAssetsByPort, isProcessing, onFlowSubmit, fieldErrors, t],
  );

  const isSubmitDisabled = isProcessing || flowTemplate.input_ports.length === 0;

  return (
    <form
      onSubmit={handleSubmit}
      className='tw-flex tw-w-full tw-flex-col tw-gap-6 tw-rounded-[30px] tw-bg-white tw-p-6 tw-shadow-[0px_0px_1px_0px_rgba(0,0,0,0.4),0px_1px_2px_0px_rgba(0,0,0,0.04),0px_2px_2px_0px_rgba(0,0,0,0.04)]'
    >
      <div
        className={clsx(
          "tw-flex tw-flex-col tw-gap-6 tw-transition-opacity tw-duration-300",
          rateLimited && "tw-pointer-events-none tw-opacity-50",
        )}
        aria-hidden={rateLimited || undefined}
      >
        {flowTemplate.input_ports.map(port => {
          const inputElementId = `flow-template-input-${port.id}`;
          const exampleValue = flowTemplateExample?.inputs[port.id];
          const exampleLabel = t("flow_use_this_example");
          return (
            <div key={port.id} className='tw-flex tw-flex-col'>
              <div className='tw-flex tw-items-center tw-gap-1.5'>
                <FlowTemplateModalityIcon modality={port.modality} className='tw-size-3.5' />
                <label
                  htmlFor={inputElementId}
                  // tw-py-1: capsize-trimmed `tw-type-sm` snaps to baseline, so
                  // `tw-truncate` (overflow:hidden) would otherwise clip
                  // descenders like the `g` in "Image".
                  className='tw-truncate tw-type-sm tw-py-1 tw-font-medium tw-text-black'
                >
                  {capitalizeFirst(port.label) || t(`flow_default_label_${port.modality}` as const)}
                </label>
              </div>
              {port.description && <p className='tw-mt-1 tw-type-xs tw-text-gray-500'>{port.description}</p>}
              <div className='tw-mt-3'>
                {port.modality === "text" ? (
                  <FlowTextField
                    inputId={inputElementId}
                    value={texts[port.id] ?? ""}
                    placeholder={t("flow_text_placeholder")}
                    disabled={fieldsDisabled}
                    onChange={value => handleTextChange(port.id, value)}
                    exampleRow={
                      exampleValue?.kind === "text" && exampleValue.value.length > 0 ? (
                        <FlowExampleRow
                          label={exampleLabel}
                          textPreview={exampleValue.value}
                          disabled={fieldsDisabled}
                          onClick={() => handleTextChange(port.id, exampleValue.value)}
                        />
                      ) : null
                    }
                  />
                ) : (
                  <FlowFileField
                    inputId={inputElementId}
                    modality={port.modality}
                    file={files[port.id] ?? null}
                    prefilledAsset={prefilledAssetsByPort[port.id] ?? null}
                    disabled={fieldsDisabled}
                    onChange={file => handleFileChange(port.id, file)}
                    onRemovePrefilledAsset={() => handleRemovePrefilledAsset(port.id)}
                    emptyLabel={t("flow_dropzone_empty")}
                    dragLabel={t("flow_dropzone_drag")}
                    removeLabel={t("flow_remove_file")}
                    exampleRow={
                      exampleValue?.kind === "asset" ? (
                        <FlowExampleRow
                          label={exampleLabel}
                          assetPreview={{ previewUrl: exampleValue.previewUrl, modality: port.modality }}
                          disabled={fieldsDisabled}
                          onClick={() =>
                            handleUseExampleAsset(port.id, {
                              assetId: exampleValue.assetId,
                              previewUrl: exampleValue.previewUrl,
                            })
                          }
                        />
                      ) : null
                    }
                  />
                )}
              </div>
              {fieldErrors[port.id] && <p className='tw-mt-2 tw-type-xs tw-text-red-500'>{fieldErrors[port.id]}</p>}
            </div>
          );
        })}
      </div>

      {error && !rateLimited && <p className='tw-type-xs tw-text-red-500'>{error}</p>}

      {rateLimited ? (
        <div className='tw-flex tw-flex-col tw-gap-4'>
          <p className='tw-type-base tw-font-medium tw-text-black'>
            {t(isVideo ? "rate_limit_description_video" : "rate_limit_description")}
          </p>
          <LinkTracked
            href={redirectCtaUrl ?? publicUrls.signUp}
            asButton
            buttonVariants={{ variant: "primary", size: "lg", expand: true }}
            eventName={EventName.mw_creative_template_omnibox_limit_sign_up_clicked}
            eventProperties={{ template_category: templateCategory ?? "", variant_slug: variantSlug ?? "" }}
          >
            {t(isVideo ? "rate_limit_button_video" : "rate_limit_button")}
          </LinkTracked>
        </div>
      ) : (
        <Button
          type='submit'
          variant='primary'
          size='lg'
          expand
          disabled={isSubmitDisabled}
          icon={isProcessing ? <IconLoader className={`${iconStrokeFix} tw-size-4 tw-animate-spin`} /> : undefined}
          iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
        >
          {step === "uploading"
            ? t("generate_button_uploading")
            : step === "generating"
              ? t("generate_button_running")
              : t("generate_button")}
        </Button>
      )}
    </form>
  );
}

function FlowTextField({
  inputId,
  value,
  placeholder,
  disabled,
  onChange,
  exampleRow,
}: {
  inputId: string;
  value: string;
  placeholder: string;
  disabled: boolean;
  onChange: (value: string) => void;
  exampleRow?: React.ReactNode;
}) {
  return (
    <div className='tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-black/10 tw-bg-white'>
      <textarea
        id={inputId}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        disabled={disabled}
        className='tw-block tw-w-full tw-min-h-[100px] tw-max-h-[200px] tw-resize-none tw-bg-transparent tw-px-3 tw-py-2.5 tw-type-sm tw-text-dark tw-placeholder-black/40 tw-outline-none focus:tw-outline-focus disabled:tw-cursor-not-allowed hover:tw-resize-y'
      />
      {exampleRow}
    </div>
  );
}

type PreviewKind = Exclude<FlowTemplatePort["modality"], "text">;

function FilePreviewMedia({ src, kind, alt }: { src: string; kind: PreviewKind; alt: string }) {
  if (kind === "video") {
    return (
      <video src={src} muted playsInline className='tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-contain' />
    );
  }
  if (kind === "audio") {
    return (
      <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-px-4'>
        {/* biome-ignore lint/a11y/useMediaCaption: user-provided audio has no captions track */}
        <audio src={src} controls className='tw-w-full' />
      </div>
    );
  }
  return (
    // biome-ignore lint/performance/noImgElement: object URL / signed-url proxy — next/image requires known dimensions
    <img src={src} alt={alt} className='tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-contain' />
  );
}

function FilePreviewCard({
  src,
  kind,
  alt,
  removeLabel,
  disabled,
  onRemove,
  exampleRow,
}: {
  src: string;
  kind: PreviewKind;
  alt: string;
  removeLabel: string;
  disabled: boolean;
  onRemove: () => void;
  exampleRow?: React.ReactNode;
}) {
  return (
    <div className='tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-black/10 tw-bg-white'>
      <div className='tw-relative tw-h-40 tw-w-full tw-isolate'>
        <FilePreviewMedia src={src} kind={kind} alt={alt} />
        {/* pointer-events-none so the overlay doesn't swallow clicks on the
            audio controls beneath it; the remove button re-enables them. */}
        <div className='tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-start tw-justify-end tw-p-1.5'>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  onClick={e => {
                    e.stopPropagation();
                    onRemove();
                  }}
                  aria-label={removeLabel}
                  disabled={disabled}
                  className='tw-pointer-events-auto tw-flex tw-size-6 tw-items-center tw-justify-center tw-rounded-lg tw-bg-black/40 tw-text-white tw-backdrop-blur tw-outline-none focus-visible:tw-outline-focus hover:tw-bg-black/50 active:tw-bg-black/60 disabled:tw-cursor-not-allowed'
                >
                  <IconCrossSmall className={`${iconStrokeFix} tw-size-3.5`} />
                </button>
              </TooltipTrigger>
              <TooltipContent variant='dark-new'>{removeLabel}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      {exampleRow}
    </div>
  );
}

function FlowFileField({
  inputId,
  modality,
  file,
  prefilledAsset,
  disabled,
  onChange,
  onRemovePrefilledAsset,
  emptyLabel,
  dragLabel,
  removeLabel,
  exampleRow,
}: {
  inputId: string;
  modality: Exclude<FlowTemplatePort["modality"], "text">;
  file: File | null;
  prefilledAsset: PrefilledAsset | null;
  disabled: boolean;
  onChange: (file: File | null) => void;
  onRemovePrefilledAsset: () => void;
  emptyLabel: string;
  dragLabel: string;
  removeLabel: string;
  exampleRow?: React.ReactNode;
}) {
  const previewUrlRef = useRef<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    if (file) {
      const url = URL.createObjectURL(file);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [file]);

  const accept = useMemo(
    () => Object.fromEntries(ACCEPTED_MIME_TYPES[modality].split(",").map(m => [m.trim(), []])),
    [modality],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept,
    multiple: false,
    noClick: true,
    disabled,
    onDropAccepted: accepted => {
      const next = accepted[0];
      if (next) onChange(next);
    },
  });

  if (file && previewUrl) {
    // Drive the element off the picked file's mime so audio doesn't render as
    // a broken <img>.
    const kind: PreviewKind = file.type.startsWith("video/")
      ? "video"
      : file.type.startsWith("audio/")
        ? "audio"
        : "image";
    return (
      <FilePreviewCard
        src={previewUrl}
        kind={kind}
        alt={file.name}
        removeLabel={removeLabel}
        disabled={disabled}
        onRemove={() => onChange(null)}
        exampleRow={exampleRow}
      />
    );
  }

  if (prefilledAsset) {
    // Use the port's modality (known by the schema) as the source of truth so
    // a video/audio asset whose `mime_type` was missing on xi still renders as
    // the right element.
    return (
      <FilePreviewCard
        src={prefilledAsset.previewUrl}
        kind={modality}
        alt=''
        removeLabel={removeLabel}
        disabled={disabled}
        onRemove={onRemovePrefilledAsset}
        exampleRow={exampleRow}
      />
    );
  }

  return (
    <div
      className={clsx(
        "tw-overflow-hidden tw-rounded-[10px] tw-border tw-bg-white tw-transition tw-duration-200",
        isDragActive ? "tw-border-black/40" : "tw-border-black/10",
        disabled && "tw-opacity-50",
      )}
    >
      <div
        {...getRootProps({
          onClick: disabled ? undefined : open,
          className: clsx(
            "tw-flex tw-h-24 tw-w-full tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-px-3 tw-text-center tw-transition tw-duration-200 tw-outline-none focus-visible:tw-outline-focus",
            isDragActive ? "tw-bg-black/[0.04]" : "hover:tw-bg-black/[0.02]",
            disabled && "tw-cursor-not-allowed",
          ),
        })}
      >
        <input id={inputId} {...getInputProps()} />
        <IconCloudSimpleUpload className={`${iconStrokeFix} tw-size-4 tw-text-gray-500`} />
        <span className='tw-type-sm tw-font-medium tw-text-dark'>{isDragActive ? dragLabel : emptyLabel}</span>
      </div>
      {exampleRow}
    </div>
  );
}

function FlowExampleRow({
  label,
  textPreview,
  assetPreview,
  disabled,
  onClick,
}: {
  label: string;
  textPreview?: string;
  assetPreview?: { previewUrl: string; modality: FlowTemplatePort["modality"] };
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      className='tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-b-[10px] tw-border-t tw-border-black/10 tw-px-2.5 tw-py-2 tw-text-left tw-outline-none tw-transition-colors hover:tw-bg-black/[0.04] focus-visible:tw-outline-focus disabled:tw-cursor-not-allowed disabled:tw-opacity-50'
    >
      <div className='tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-overflow-hidden'>
        {assetPreview &&
          (assetPreview.modality === "video" ? (
            <video
              src={assetPreview.previewUrl}
              muted
              playsInline
              className='tw-size-7 tw-shrink-0 tw-rounded-[6px] tw-border tw-border-black/10 tw-object-cover'
            />
          ) : assetPreview.modality === "audio" ? (
            // A 28px <audio controls> doesn't fit; show the modality icon instead.
            <span className='tw-flex tw-size-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[6px] tw-border tw-border-black/10 tw-bg-black/[0.04]'>
              <FlowTemplateModalityIcon modality='audio' className='tw-size-3.5' />
            </span>
          ) : (
            <Image
              src={assetPreview.previewUrl}
              alt=''
              width={28}
              height={28}
              sizes='28px'
              className='tw-size-7 tw-shrink-0 tw-rounded-[6px] tw-border tw-border-black/10 tw-object-cover'
            />
          ))}
        {/* tw-py-1: capsize-trimmed `tw-type-xs` snaps to baseline, so
            `tw-truncate` (overflow:hidden) would clip descenders like `g`/`p`. */}
        <span className='tw-shrink-0 tw-py-1 tw-type-xs tw-font-medium tw-text-black'>{label}</span>
        {textPreview && (
          <span className='tw-min-w-0 tw-truncate tw-py-1 tw-type-xs tw-text-gray-500'>{textPreview}</span>
        )}
      </div>
      <IconChevronRight className={`${iconStrokeFix} tw-ml-2 tw-size-3.5 tw-shrink-0 tw-text-gray-500`} />
    </button>
  );
}
